import { useUpdateFoodSupplement } from '@/api/food-supplement/food-supplement.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  FoodSupplement,
  UpdateFoodSupplementRequest,
} from '@repo/types/src/food-supplement';
import { Edit } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type EditFoodSupplementProps = {
  item: FoodSupplement;
  refetch: () => void;
};

export const EditFoodSupplement = ({
  item,
  refetch,
}: EditFoodSupplementProps) => {
  const mutation = useUpdateFoodSupplement();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: item.name,
    taste: item.taste ?? '',
    dosagePerDay: item.dosagePerDay?.toString() ?? '',
    descriptionTitle: item.descriptionTitle ?? '',
    description: item.description ?? '',
    image: item.image ?? '',
    link: item.link ?? '',
    fastActing: item.fastActing,
  });

  const handleUpdate = () => {
    if (!formData.name) {
      toast.error('Le nom est obligatoire');
      return;
    }

    const payload: UpdateFoodSupplementRequest = {
      name: formData.name,
      taste: formData.taste || undefined,
      dosagePerDay: formData.dosagePerDay
        ? parseInt(formData.dosagePerDay, 10)
        : undefined,
      descriptionTitle: formData.descriptionTitle || undefined,
      description: formData.description || undefined,
      image: formData.image || undefined,
      link: formData.link || undefined,
      fastActing: formData.fastActing,
    };

    mutation.mutate(
      { id: item.id, payload },
      {
        onSuccess: (data) => {
          setOpen(false);
          refetch();
          toast.success(`${data.name} a été modifié avec succès!`, {
            duration: 3000,
          });
        },
        onError: () => {
          toast.error(`Erreur lors de la modification`, { duration: 3000 });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">
            Modifier le complément alimentaire
          </DialogTitle>
          <DialogDescription className="text-center">
            Modifier les informations du complément
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-3">
          <div className="flex flex-col gap-1">
            <Label>Nom *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Goût</Label>
            <Input
              type="text"
              value={formData.taste}
              onChange={(e) =>
                setFormData({ ...formData, taste: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Dosage par jour</Label>
            <Input
              type="number"
              value={formData.dosagePerDay}
              min={1}
              onChange={(e) =>
                setFormData({ ...formData, dosagePerDay: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Titre de la description</Label>
            <Input
              type="text"
              value={formData.descriptionTitle}
              onChange={(e) =>
                setFormData({ ...formData, descriptionTitle: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Description</Label>
            <Input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>URL de l&apos;image</Label>
            <Input
              type="text"
              value={formData.image}
              onChange={(e) =>
                setFormData({ ...formData, image: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Lien</Label>
            <Input
              type="url"
              value={formData.link}
              onChange={(e) =>
                setFormData({ ...formData, link: e.target.value })
              }
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={formData.fastActing}
              onChange={(e) =>
                setFormData({ ...formData, fastActing: e.target.checked })
              }
            />
            Action rapide
          </label>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Annuler</Button>
          </DialogClose>
          <Button
            variant="default"
            onClick={handleUpdate}
            disabled={mutation.isPending}
          >
            Modifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
